import React, { useEffect } from 'react'
import { navigate } from 'gatsby'
import { usePrismicPreview } from 'gatsby-source-prismic'
import { pathFor, linkResolverPathFor } from '@walltowall/helpers'

import { useAllSitePages } from '../hooks/useAllSitePages'
import { useCustomTypePageMap } from '../hooks/useCustomTypePageMap'
import { usePrismicRepositoryName } from '../hooks/usePrismicRepositoryName'
import { usePrismicPreviewStoreActions } from '../hooks/usePrismicPreviewStore'

import { Spinner } from '../components/Spinner'
import { InvalidPreview } from '../components/InvalidPreview'
import { ErrorBoundary } from '../components/ErrorBoundary'

export const BasePreviewTemplate = ({
  linkResolver = () => linkResolverPathFor,
  pathResolver,
}) => {
  const repositoryName = usePrismicRepositoryName()
  const allSitePages = useAllSitePages()
  const customTypePageMap = useCustomTypePageMap()
  const { setPage } = usePrismicPreviewStoreActions()

  const { isLoading, isPreview, previewData, path } = usePrismicPreview({
    repositoryName,
    linkResolver,
    pathResolver:
      pathResolver ||
      ((...args) => doc => {
        const linkResolverForNode = linkResolver(...args)
        const previewPath = linkResolverForNode(doc)

        // Check if the doc's type has a custom page mapping. If it exists,
        // return it.
        if (customTypePageMap.hasOwnProperty(doc.type))
          return pathFor(customTypePageMap.doc.type)

        // If page exists, return the existing page.
        if (allSitePages.some(page => page.path === previewPath))
          return previewPath

        // If a custom type page mapping does not exist, return the unpublished
        // preview page.
        return '/unpublishedPreview'
      }),
  })

  useEffect(() => {
    if (isPreview && previewData && path) {
      setPage(path, previewData)
      navigate(path)
    }
  }, [isLoading, isPreview, path, previewData, setPage])

  if (isPreview === false)
    return (
      <InvalidPreview>
        You're on the preview page, but it looks like we don't have any data to
        preview!
      </InvalidPreview>
    )

  return <Spinner />
}

export const PreviewTemplate = props => (
  <ErrorBoundary
    fallback={
      <InvalidPreview>
        Oh no! Looks like something went wrong with this preview. Please try
        again.
      </InvalidPreview>
    }
  >
    <BasePreviewTemplate {...props} />
  </ErrorBoundary>
)
